import { GenericDevice, NewGenericDevice } from "@/data/models/GenericDevice";
import {
  createDevice,
  createImportHistory,
  createUpdatedDevice,
  getCategoryName,
  getCategoryIdFromName,
  getClientDevicesRef,
  getDeviceName,
  getDeviceTemplateIdFromName,
  getMaxInternalDeviceNumber,
  saveDevices,
  storeImportHistory,
  updateDevice,
  updateDevices,
  clientPanelToGenericDevice,
  getPowerUsageTypeFromDeviceAttributeType,
} from "@/data/repository/GenericDeviceRepository";
import { createLine, DUPLICATE_STATUS_KEY_ID, ImportType } from "@/utils/devices.csv";
import { isInventoryNumberValid, isQrCodeValid } from "@/data/models/BaseDeviceInfo";
import Vue from "vue";
import { i18n } from "@/plugins/vuetify";
import {
  getClientPanelsRef,
  updateClientPanels,
} from "@/data/repository/ClientPanelRepository";
import { firestore } from "firebase";
import {
  getReleasedQrCode,
  getUpdatedIdAfterDeletion,
  filterOutUndefined,
  isNotEmpty,
  splitIntoBatches,
  areTrimmedStringsDifferent,
  areTrimmedStringsEqualIgnoringCase,
  CSV_SEPARATOR,
} from "@/utils/Utils";
import {
  addEditTemplate,
  addNewCategory,
  DeviceSettingsJson,
  isIresCategory,
  saveDeviceSettings,
} from "@/data/models/DeviceSettingsManager";
import { GenericDeviceSettingsDeprecated } from "@/data/models/Client";
import { DevicePowerUsageType } from "@/data/models/DeviceAttribute";
import {
  DeviceUpdate,
  ImportError,
  ImportErrorType,
  ImportProgress,
  ImportResult,
} from "@/data/models/CSV";
import { isDeviceDeleted, shouldDeleteDevice } from "./DeviceManagementUtils";
import { getIresDeviceElectricityMeterManufacturer, getIresDeviceManufacturer, getIresDevicePowerConsumptionPerHour, getIresDevicePowerUsage } from "@/data/models/IresDevice";
import ClientPanel from "@/data/models/ClientPanel";
import { defineCategoriesAndTemplatesClientAccess } from "@/permissions/ClientPermissions";
import { removeDeviceFromVehicle } from "@/data/repository/VehicleRepository";
import {
  findBranchByNumber,
  getBranchesOfClient,
  getVehicleManagementAccess,
} from "@/data/repository/ClientRepository";
import { UNASSIGNED_BRANCH_NUMBER } from "@/data/models/Branch";
import { getDeviceHomeLocation } from "@/utils/VehicleUtils";

export class DeviceImporter {
  private newlyCreatedDevices: GenericDevice[] = [];
  private updatedDevices: GenericDevice[] = [];
  private updatedIresDevices: NewGenericDevice[] = [];
  private errors: ImportError[] = [];
  private processedDevices = 0;
  private noOfDuplicateDevices = 0;
  private statuses = new Map<string, string>();
  private isAborted = false;
  private devicesToUpdate: DeviceUpdate[] = [];
  private devicesToSave: GenericDevice[] = [];
  private newCategories: string[] = [];
  private newTemplates: string[] = [];
  public reportData: string[] = [];
  private vueInst = new Vue({ i18n });
  private progressCallback: (progress: ImportProgress) => void = () => {
    null;
  };

  /**
   *
   * @param devices -> list of devices to import
   * @param currentDevices -> list of currently stored devices (panels)
   * @param clientPanels -> list of currently stored devices (generic devices)
   * @param fileName -> imported file name to use on import history
   * @param deviceSettings -> device settings data
   * @param clientSettings -> determines whether new categories/templates can be created or updated for existing devices
   * @param dryRun -> when true, runs validation only without writing to Firestore
   */
  constructor(
    private devices: NewGenericDevice[],
    private currentDevices: GenericDevice[],
    private clientPanels: ClientPanel[],
    private fileName: string,
    private deviceSettings: DeviceSettingsJson,
    private clientSettings: GenericDeviceSettingsDeprecated,
    private dryRun = false,
  ) {}

  /**
   * Sets a callback function to observe the progress of the device import.
   *
   * @param onProgress A callback function that receives an ImportProgress object.
   *                   This function will be invoked to report progress during the import process.
   * @returns {void}
   */
  public observeProgress(onProgress: (progress: ImportProgress) => void): void {
    this.progressCallback = (progress) => {
      onProgress(progress);
    };
  }

  /**
   * Builds a single CSV report row for a device, including its import status and any error message.
   */
  private buildReportRow(
    device: NewGenericDevice | GenericDevice,
    iresDevice: boolean,
    identifier: string,
    statusKey: string,
  ): string {
    const deviceName = device.deviceName
      ? device.deviceName
      : getDeviceName(
          this.deviceSettings,
          device.templateId,
          device.categoryId
        ) ?? "";

    const categoryName = (device as NewGenericDevice).categoryName
      ? (device as NewGenericDevice).categoryName
      : getCategoryName(this.deviceSettings, device.categoryId) ?? "";
    let row = createLine(
      this.deviceSettings,
      categoryName,
      deviceName,
      device,
      iresDevice ? null : device.lastMeterReading,
      iresDevice
    );

    const error = this.errors.find((err) => err.inventoryNumber === identifier);
    
    const status1 = this.statuses.get(statusKey);
    const status2 = this.statuses.get(identifier);

    const status = status1 && error ? status1 : (status2 || "");
    
    row = [...row, status];

    if (error) {
      const errorMsg = error.conflictingDeviceId
        ? this.vueInst.$t(this.getErrorMessage(error.message), { conflictId: error.conflictingDeviceId }).toString()
        : this.vueInst.$t(this.getErrorMessage(error.message)).toString();
      row = [...row, errorMsg];
    }

    return row.join(CSV_SEPARATOR);
  }

  /**
   * Emits progress through progressCallback
   * @param device
   * @param iresDevice
   */
  emitProgress(
    device: NewGenericDevice | GenericDevice, 
    iresDevice = false,
    customIdentifier?: string,
  ) {
    const identifier: string = customIdentifier
      ?? ((iresDevice ? device.serialNumber : device.inventoryNumber)
      || `_unknown_${this.processedDevices}`);

    const statusKey = customIdentifier ?? DUPLICATE_STATUS_KEY_ID+identifier;

    this.reportData.push(this.buildReportRow(device, iresDevice, identifier, statusKey));

    this.processedDevices++;
    this.progressCallback({
      totalDevices: this.devices.length,
      processedDevices: this.processedDevices,
      errors: this.errors,
      updatedDevices:
        this.updatedDevices.length + this.updatedIresDevices.length,
      newDevices: this.newlyCreatedDevices.length,
      report: this.reportData,
      criticalFailure: this.errors.length >= this.devices.length / 2,
      duplicatedDevices: this.noOfDuplicateDevices,
      dryRun: this.dryRun,
    });
  }

  /** Fields to compare when determining if a device has been updated via CSV import. */
  private static readonly DEVICE_UPDATE_FIELDS: ReadonlyArray<{
    key: string;
    existingDefault?: string;
    /** Reads the value to compare against when it is not simply `existingDevice[key]`. */
    existingValue?: (device: GenericDevice) => unknown;
  }> = [
    { key: 'categoryId' },
    { key: 'templateId' },
    { key: 'inventoryNumber' },
    { key: 'serialNumber' },
    { key: 'manufacturer', existingDefault: '' },
    { key: 'qrCode', existingDefault: '' },
    { key: 'powerConsumptionPerHour', existingDefault: '' },
    { key: 'devicePowerUsageType', existingDefault: '' },
    { key: 'selectedPowerConsumption', existingDefault: '' },
    { key: 'electricityMeterManufacturer', existingDefault: '' },
    { key: 'electricityMeterNumber', existingDefault: '' },
    { key: 'acquisitionDate', existingDefault: '' },
    { key: 'availability' },
    // The CSV's STANDORT column is the home location, and a device that never had one
    // belongs to where it stands, so an unchanged file must not read as an update.
    { key: 'homeLocation', existingDefault: '', existingValue: getDeviceHomeLocation },
    { key: 'note', existingDefault: '' },
    { key: 'lastMeterReading', existingDefault: '' },
  ];

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  isDeviceUpdated(existingDevice: GenericDevice, updatedDevice: any): boolean {
    return DeviceImporter.DEVICE_UPDATE_FIELDS.some(({ key, existingDefault, existingValue }) => {
      const updatedVal = updatedDevice[key];
      if (!updatedVal || !isNotEmpty(updatedVal.toString())) {
        return false;
      }
      const existingVal = existingValue
        ? existingValue(existingDevice)
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        : (existingDevice as any)[key];
      const existingStr = existingDefault !== undefined
        ? (existingVal?.toString() ?? existingDefault)
        : (existingVal?.toString() ?? '');
      return areTrimmedStringsDifferent(updatedVal.toString(), existingStr);
    });
  }

  /**
   * Whether the STANDORT column of a row may be written to the device.
   *
   * The column carries a branch **number**, the same token the Cloud `/devices`
   * endpoint takes as `branchNumber`, and an unknown number would leave the device
   * with a location that resolves to nothing in every list and filter. Three values
   * pass regardless, for reasons the data gives us:
   *
   * - an empty column is "no value supplied", not "clear the location";
   * - `0` is what a deleted branch leaves on its devices, so it has to round-trip;
   * - a value equal to what the device already carries is not a change, so a row
   *   editing some other field is not blocked by a location that was already stale.
   *   This is the same rule the Cloud service applies (it validates `branchNumber`
   *   only when it differs from the stored `branch`).
   *
   * Clients that define no branches at all have nothing to validate against, and
   * some of them do carry branch numbers on their devices, so they are skipped.
   */
  private isBranchNumberValid(
    csvValue: string | null | undefined,
    existingValue: string | null | undefined
  ): boolean {
    if (!csvValue || !isNotEmpty(csvValue)) {
      return true;
    }

    const branchNumber = csvValue.trim();

    if (branchNumber === UNASSIGNED_BRANCH_NUMBER) {
      return true;
    }

    if (existingValue && !areTrimmedStringsDifferent(branchNumber, existingValue.toString())) {
      return true;
    }

    if (getBranchesOfClient().length === 0) {
      return true;
    }

    return findBranchByNumber(branchNumber) !== undefined;
  }

  /**
   * Applies the STANDORT column of a row to an existing device.
   *
   * The column names the branch the device belongs to. Its current Standort follows
   * only when vehicle management is off, where the two are the same thing by
   * definition and the device dialog mirrors them on save as well. With vehicle
   * management on, the current Standort is owned by the load and unload flows, so an
   * inventory import must not move a device out of a vehicle or another warehouse.
   *
   * An empty column leaves both fields as they are.
   */
  private applyLocationOfExistingDevice(
    device: NewGenericDevice,
    existingDevice: { branch?: string | null; homeLocation?: string | null }
  ) {
    const homeLocation =
      device.homeLocation ?? getDeviceHomeLocation(existingDevice) ?? undefined;

    device.homeLocation = homeLocation;
    device.branch = getVehicleManagementAccess()
      ? existingDevice.branch ?? undefined
      : homeLocation;
  }

  /**
   * Whether the row's QR code is free: not on another stored device, nor on a row
   * already accepted from the same file.
   *
   * A row without a QR code is always unique. Missing codes are equal to each other
   * (`undefined === undefined`), so without this every row after the first one without
   * a code was rejected as a duplicate.
   */
  isQrCodeUnique(
    device: NewGenericDevice,
    importType: ImportType
  ) {
    if (!isNotEmpty(device.qrCode)) {
      return true;
    }

    return !this.clientPanels.find(
      (val) =>
        val.qrCode &&
        isNotEmpty(val.qrCode) &&
        val.qrCode === device.qrCode &&
        (val.serialNumber !== device.serialNumber || importType !== ImportType.UPDATE_EXISTING_IRES_DEVICE)
    ) &&
    !this.currentDevices.find(
      (val) => 
        val.qrCode === device.qrCode &&
        (val.inventoryNumber !== device.inventoryNumber || importType !== ImportType.UPDATE_EXISTING_GENERIC_DEVICE)
    ) &&
    !this.updatedIresDevices.find(
      (val) => 
        val.qrCode === device.qrCode
    ) &&
    !this.devicesToUpdate.find(
      (val) => 
        val.newDevice.qrCode === device.qrCode
    ) &&
    !this.devicesToSave.find(
      (val) => 
        val.qrCode === device.qrCode
    );
  }

  /**
   * Finds the identifier of a device that conflicts on QR code with the given device.
   * Searches across client panels, current devices, pending updates, and pending saves.
   */
  private findConflictingQrCodeDevice(device: NewGenericDevice): string | undefined {
    const conflictingPanel = this.clientPanels.find(
      (val) => val.qrCode && isNotEmpty(val.qrCode) && val.qrCode === device.qrCode
    );
    if (conflictingPanel) return conflictingPanel.serialNumber ?? conflictingPanel.inventoryNumber;

    const conflictingCurrent = this.currentDevices.find(
      (val) => val.qrCode === device.qrCode
    );
    if (conflictingCurrent) return conflictingCurrent.inventoryNumber;

    const conflictingUpdate = this.devicesToUpdate.find(
      (val) => val.newDevice.qrCode === device.qrCode
    );
    if (conflictingUpdate) return conflictingUpdate.newDevice.inventoryNumber;

    const conflictingSave = this.devicesToSave.find(
      (val) => val.qrCode === device.qrCode
    );
    if (conflictingSave) return conflictingSave.inventoryNumber;

    return undefined;
  }

  getErrorMessage(e: ImportErrorType): string {
    switch (e) {
      case ImportErrorType.CATEGORY_NOT_FOUND:
        return "settings.hyDryDevice.errorMessageCategoryNotFound";
      case ImportErrorType.MISSING_CATEGORY:
        return "settings.hyDryDevice.errorMessageMissingCategory";
      case ImportErrorType.MISSING_DEVICE_TEMPLATE:
        return "settings.hyDryDevice.errorMessageMissingTemplate";
      case ImportErrorType.SERVER_ERROR:
        return "settings.hyDryDevice.genericError";
      case ImportErrorType.MISSING_INVENTORY_NUMBER:
        return "settings.hyDryDevice.errorMessageMissingInventoryNumber";
      case ImportErrorType.TEMPLATE_NOT_FOUND:
        return "settings.hyDryDevice.errorMessageTemplateNotFound";
      case ImportErrorType.IRES_DEVICE:
        return "settings.hyDryDevice.errorMessageIresDevice";
      case ImportErrorType.TEMPLATE_UPDATE:
        return "settings.hyDryDevice.errorMessageTemplateUpdateNotAllowed";
      case ImportErrorType.CATEGORY_UPDATE:
        return "settings.hyDryDevice.errorMessageCategoryUpdateNotAllowed";
      case ImportErrorType.BRANCH_NOT_FOUND:
        return "settings.hyDryDevice.errorMessageBranchNotFound";
      case ImportErrorType.DEVICE_DOES_NOT_EXIST:
        return "settings.hyDryDevice.errorMessageDeviceDoesNotExist";
      case ImportErrorType.INVALID_INVENTORY_NUMBER:
        return "settings.hyDryDevice.errorMessageInvalidInventoryNumber";
      case ImportErrorType.INVALID_QR_CODE:
        return "settings.hyDryDevice.errorMessageInvalidQrCode";
      case ImportErrorType.INVENTORY_NUMBER_ALREADY_EXISTS:
        return "settings.hyDryDevice.errorMessageInventoryNumberAlreadyExists";
      case ImportErrorType.QR_CODE_ALREADY_EXISTS:
        return "settings.hyDryDevice.errorMessageQrCodeAlreadyExists";
      case ImportErrorType.INVALID_MANUFACTURER:
          return "settings.hyDryDevice.errorMessageInvalidManufacturer";
      case ImportErrorType.INVALID_POWER_CONSUMPTION_PER_HOUR:
        return "settings.hyDryDevice.errorMessageInvalidPowerConsumptionPerHour";
      case ImportErrorType.INVALID_DEVICE_POWER_USAGE_TYPE:
        return "settings.hyDryDevice.errorMessageInvalidDevicePowerUsageType";
      case ImportErrorType.INVALID_ELECTRICITY_MANUFACTURER:
        return "settings.hyDryDevice.errorMessageInvalidElectricityMeterManufacturer";
      case ImportErrorType.DEVICE_ASSIGNED_TO_PROJECT:
        return "settings.hyDryDevice.errorMessageDeviceAssignedToProject";
      default:
        return "";
    }
  }

  private handleImportError(
    device: NewGenericDevice | GenericDevice,
    message: ImportErrorType,
    iresDevice = false,
    customIdentifier?: string,
    conflictingDeviceId?: string,
  ) {
    const identifier: string = customIdentifier
      ?? ((iresDevice ? device.serialNumber : device.inventoryNumber)
      || `_unknown_${this.processedDevices}`);
    
    this.errors.push({
      inventoryNumber: identifier,
      message: message,
      conflictingDeviceId: conflictingDeviceId,
    });
    
    this.statuses.set(identifier, ImportResult.ERROR);
    this.emitProgress(device, iresDevice, customIdentifier);
  }

  /**
   * triggers the device import process
   * loops through the devices and if necessary data is present, proceeds to either update or creates the device
   */
  private async importDevices() {
    const maxInternalDeviceNumber = await getMaxInternalDeviceNumber();
    for (const device of this.devices) {
      if (this.isAborted) {
        break;
      }
      
      const clientPanel = this.clientPanels.find(clientPanel => 
        clientPanel.serialNumber === device.serialNumber
      );

      const existingDevice = this.currentDevices.find((dev) =>
        dev.inventoryNumber
          ? dev.inventoryNumber === device.inventoryNumber
          : false
      );

      if (clientPanel) {
        const existingIresDevice = clientPanel;
        
        if (isDeviceDeleted(existingIresDevice)) {
          this.handleImportError(
            device,
            ImportErrorType.DEVICE_DOES_NOT_EXIST,
            true
          );
          continue;
        } 

        // Validated before the delete branch below, which renames the device and takes
        // it out of its vehicle: a row rejected after that would leave those behind.
        if (!this.isBranchNumberValid(device.homeLocation, getDeviceHomeLocation(existingIresDevice))) {
          this.handleImportError(device, ImportErrorType.BRANCH_NOT_FOUND, true);
          continue;
        }

        const shouldDelete = shouldDeleteDevice(device);

        if (shouldDelete && existingIresDevice.currentProjectId) {
          this.handleImportError(
            device,
            ImportErrorType.DEVICE_ASSIGNED_TO_PROJECT,
            true
          );
          continue;
        }

        if (isNotEmpty(device.inventoryNumber) && !isInventoryNumberValid(device.inventoryNumber)) {
          this.handleImportError(
            device,
            ImportErrorType.INVALID_INVENTORY_NUMBER,
            true
          );
          continue;
        }

        const isInventoryNumberUnique =
          this.clientPanels.filter(
            (val) =>
              val.inventoryNumber &&
              isNotEmpty(val.inventoryNumber) &&
              val.inventoryNumber === device.inventoryNumber
          ).length < 2   &&
          !this.currentDevices.find(
            (val) => 
              val.inventoryNumber === device.inventoryNumber
          );

        if (!isInventoryNumberUnique) {
          const conflictingDevice = this.currentDevices.find(
            (val) => val.inventoryNumber === device.inventoryNumber
          );
          this.handleImportError(
            device,
            ImportErrorType.INVENTORY_NUMBER_ALREADY_EXISTS,
            true,
            undefined,
            conflictingDevice?.inventoryNumber ?? device.inventoryNumber,
          );
          continue;
        }  

        const isQrCodeUnique = this.isQrCodeUnique(
          device,
          ImportType.UPDATE_EXISTING_IRES_DEVICE
        );

        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        const serialNumber = device.serialNumber!!;

        if (!isQrCodeUnique) {
          const statusKey = DUPLICATE_STATUS_KEY_ID+serialNumber;
          const isAlreadyProcessed = this.statuses.get(serialNumber);

          this.handleImportError(
            device,
            ImportErrorType.QR_CODE_ALREADY_EXISTS,
            true,
            isAlreadyProcessed ? statusKey : undefined,
            this.findConflictingQrCodeDevice(device),
          );

          continue;
        }  

        if (device.qrCode && isNotEmpty(device.qrCode) && !isQrCodeValid(device.qrCode)) {
          this.handleImportError(
            device,
            ImportErrorType.INVALID_QR_CODE,
            true
          );
          continue;
        }

        if (
          device.manufacturer && 
          isNotEmpty(device.manufacturer) && 
          !areTrimmedStringsEqualIgnoringCase(device.manufacturer, getIresDeviceManufacturer())
        ) {
          this.handleImportError(
            device,
            ImportErrorType.INVALID_MANUFACTURER,
            true
          );
          continue;
        }

        if (
          device.powerConsumptionPerHour && 
          device.powerConsumptionPerHour !== getIresDevicePowerConsumptionPerHour(existingIresDevice.serialNumber)
        ) {
          this.handleImportError(
            device,
            ImportErrorType.INVALID_POWER_CONSUMPTION_PER_HOUR,
            true
          );
          continue;
        }

        if (
          device.devicePowerUsageType &&
          device.devicePowerUsageType !== getIresDevicePowerUsage(existingIresDevice.serialNumber)
        ) {
          this.handleImportError(
            device,
            ImportErrorType.INVALID_DEVICE_POWER_USAGE_TYPE,
            true
          );
          continue;
        }

        if (
          device.electricityMeterManufacturer && 
          isNotEmpty(device.electricityMeterManufacturer) && 
          !areTrimmedStringsEqualIgnoringCase(device.electricityMeterManufacturer, getIresDeviceElectricityMeterManufacturer())
        ) {
          this.handleImportError(
            device,
            ImportErrorType.INVALID_ELECTRICITY_MANUFACTURER,
            true
          );
          continue;
        }

        this.applyLocationOfExistingDevice(device, existingIresDevice);

        // Deletion runs last, after every check the row can still fail. It suffixes the
        // inventory number with the deletion separator, which the validity check above
        // rejects, and it empties the device out of its vehicle: both have to happen on
        // a row that is going to be written. This mirrors the generic device path.
        if (shouldDelete && !this.dryRun) {
          const deletedInventoryNumber = await getUpdatedIdAfterDeletion(
            device.inventoryNumber ?? "",
            "inventoryNumber",
            getClientPanelsRef(),
          );

          if (deletedInventoryNumber === "") {
            this.handleImportError(device, ImportErrorType.SERVER_ERROR, true);
            continue;
          }

          // The QR code is released with it, so the label can go on another device. An empty
          // CODE column keeps the stored code, so that is the one to release.
          const releasedQrCode = await getReleasedQrCode(
            device.qrCode ?? existingIresDevice.qrCode,
            getClientPanelsRef(),
          );

          if (releasedQrCode === "") {
            this.handleImportError(device, ImportErrorType.SERVER_ERROR, true);
            continue;
          }

          // The vehicle is read off the stored panel: the parsed CSV row has no
          // vehicleId column, so this used to be undefined and the device stayed
          // listed in its vehicle after being deleted.
          const vehicleId = existingIresDevice.vehicleId;

          device.inventoryNumber = deletedInventoryNumber;
          device.vehicleId = null;

          if (releasedQrCode) {
            device.qrCode = releasedQrCode;
          }

          if (vehicleId && device.serialNumber) {
            await removeDeviceFromVehicle(vehicleId, device.serialNumber);
          }
        }

        if (!this.statuses.get(serialNumber)) {
          this.statuses.set(serialNumber, shouldDelete ? ImportResult.DELETED : ImportResult.UPDATED);
          this.updatedIresDevices.push(device);
        }
        else {
          const statusKey = DUPLICATE_STATUS_KEY_ID+serialNumber;
          this.statuses.set(statusKey, ImportResult.DUPLICATE);
          this.noOfDuplicateDevices++;
          this.emitProgress(device, true, statusKey);
        }

        continue;
      }

      if (existingDevice) {
        const isDeleted = isDeviceDeleted(existingDevice);

        if (isDeleted) {
          this.handleImportError(
            device,
            ImportErrorType.DEVICE_DOES_NOT_EXIST,
          );
          continue;
        }

        if (isNotEmpty(device.categoryName)) {
          device.categoryId =
            (await this.getDeviceCategoryId(device.categoryName)) ?? "";
        }

        if (isNotEmpty(device.deviceName) && isNotEmpty(device.categoryId)) {
          device.templateId =
            (await this.getDeviceTemplateId(
              device.deviceName,
              device.categoryId,
              getPowerUsageTypeFromDeviceAttributeType(device.selectedPowerConsumption ?? undefined),
              device.powerConsumptionPerHour ?? undefined
            )) ?? "";
        }

        if (!this.isBranchNumberValid(device.homeLocation, getDeviceHomeLocation(existingDevice))) {
          this.handleImportError(device, ImportErrorType.BRANCH_NOT_FOUND);
          continue;
        }

        this.applyLocationOfExistingDevice(device, existingDevice);

        const deviceUpdate = createUpdatedDevice(device, existingDevice);
        
        if (!isNotEmpty(deviceUpdate.categoryId)) {
          this.handleImportError(device, ImportErrorType.MISSING_CATEGORY);
          continue;
        }

        if (!isNotEmpty(deviceUpdate.templateId)) {
          this.handleImportError(
            device,
            ImportErrorType.MISSING_DEVICE_TEMPLATE
          );
          continue;
        }

        if (!isNotEmpty(deviceUpdate.inventoryNumber)) {
          this.handleImportError(
            device,
            ImportErrorType.MISSING_INVENTORY_NUMBER
          );
          continue;
        }

        if (!isInventoryNumberValid(deviceUpdate.inventoryNumber)) {
          this.handleImportError(
            device,
            ImportErrorType.INVALID_INVENTORY_NUMBER
          );
          continue;
        }

        const isInventoryNumberUnique =
          !this.clientPanels.find(
            (val) =>
              val.inventoryNumber &&
              isNotEmpty(val.inventoryNumber) &&
              val.inventoryNumber === device.inventoryNumber
          ) &&
          this.currentDevices.filter(
            (val) => 
              val.inventoryNumber === device.inventoryNumber
          ).length === 1;

        if (!isInventoryNumberUnique) {
          const conflictingPanel = this.clientPanels.find(
            (val) => val.inventoryNumber && isNotEmpty(val.inventoryNumber) && val.inventoryNumber === device.inventoryNumber
          );
          this.handleImportError(
            device,
            ImportErrorType.INVENTORY_NUMBER_ALREADY_EXISTS,
            false,
            undefined,
            conflictingPanel?.serialNumber ?? conflictingPanel?.inventoryNumber ?? device.inventoryNumber,
          );
          continue;
        }

        const isQrCodeUnique = this.isQrCodeUnique(
          device,
          ImportType.UPDATE_EXISTING_GENERIC_DEVICE
        );

        if (!isQrCodeUnique) {
          const statusKey = DUPLICATE_STATUS_KEY_ID+device.inventoryNumber;
          const isAlreadyProcessed = this.statuses.get(device.inventoryNumber);

          this.handleImportError(
            device,
            ImportErrorType.QR_CODE_ALREADY_EXISTS,
            false,
            isAlreadyProcessed ? statusKey : undefined,
            this.findConflictingQrCodeDevice(device),
          );

          continue;
        }  

        if (deviceUpdate.qrCode && !isQrCodeValid(deviceUpdate.qrCode)) {
          this.handleImportError(
            device,
            ImportErrorType.INVALID_QR_CODE
          );
          continue;
        }

        if (!this.clientSettings.updateAllowed) {
          if (existingDevice.categoryId !== deviceUpdate.categoryId) {
            this.handleImportError(device, ImportErrorType.CATEGORY_UPDATE);
            continue;
          }
          
          if (existingDevice.templateId !== deviceUpdate.templateId) {
            this.handleImportError(device, ImportErrorType.TEMPLATE_UPDATE);
            continue;
          }
        }
        const updatedDevice = filterOutUndefined(deviceUpdate);

        if (!this.isDeviceUpdated(existingDevice, updatedDevice)) {
          if (!this.statuses.get(device.inventoryNumber)) {
            this.statuses.set(device.inventoryNumber, ImportResult.NO_CHANGE);
          }
          else {
            const statusKey = DUPLICATE_STATUS_KEY_ID+device.inventoryNumber;
            this.statuses.set(statusKey, ImportResult.DUPLICATE);
            this.noOfDuplicateDevices++;
          }
        
          this.emitProgress(device);
          continue;
        }

        const shouldDelete = shouldDeleteDevice(device);

        if (shouldDelete) {
          if (existingDevice.currentAssignedProjectId) {
            this.handleImportError(
              device,
              ImportErrorType.DEVICE_ASSIGNED_TO_PROJECT
            );
            continue;
          }

          if (!this.dryRun) {
            const deletedInventoryNumber = await getUpdatedIdAfterDeletion(
              existingDevice.inventoryNumber,
              "inventoryNumber",
              getClientDevicesRef()
            );

            // The QR code is released with it, so the label can go on another device.
            const releasedQrCode = await getReleasedQrCode(updatedDevice.qrCode, getClientDevicesRef());

            if (deletedInventoryNumber !== "" && releasedQrCode !== "") {
              // Read off the stored device, not the parsed CSV row, which has no
              // vehicleId column.
              const vehicleId = existingDevice.vehicleId;

              updatedDevice.inventoryNumber = deletedInventoryNumber;
              updatedDevice.vehicleId = null;

              if (releasedQrCode) {
                updatedDevice.qrCode = releasedQrCode;
              }

              if (vehicleId && existingDevice.id) {
                await removeDeviceFromVehicle(vehicleId, existingDevice.id);
              }
            }
            else {
              this.handleImportError(device, ImportErrorType.SERVER_ERROR);
              continue;
            }
          }
        }

        try {
          if (!this.statuses.get(device.inventoryNumber)) {
            this.devicesToUpdate = [
              ...this.devicesToUpdate,
              {
                oldDevice: existingDevice,
                newDevice: updatedDevice,
              },
            ];

            this.statuses.set(device.inventoryNumber, shouldDelete ? ImportResult.DELETED : ImportResult.UPDATED);
          }
          else {
            const statusKey = DUPLICATE_STATUS_KEY_ID+device.inventoryNumber;
            this.statuses.set(statusKey, ImportResult.DUPLICATE);
            this.noOfDuplicateDevices++;
            this.emitProgress(device, false, statusKey);
          }
        } 
        catch (e) {
          this.handleImportError(device, ImportErrorType.SERVER_ERROR);
        }
      } 
      else {
        if ((device.availability || device.availability === 0) && shouldDeleteDevice(device)) {
          this.handleImportError(device, ImportErrorType.DEVICE_DOES_NOT_EXIST);
          continue;
        }

        const categoryId = await this.getDeviceCategoryId(
          device.categoryName
        );
        
        if (categoryId && isIresCategory(categoryId)) {
          this.handleImportError(device, ImportErrorType.IRES_DEVICE, true);
          continue;
        }

        if (!categoryId) {
          this.handleImportError(device, ImportErrorType.CATEGORY_NOT_FOUND);
          continue;
        }
        
        const deviceTemplateId = await this.getDeviceTemplateId(
          device.deviceName,
          categoryId,
          getPowerUsageTypeFromDeviceAttributeType(device.selectedPowerConsumption ?? undefined),
          device.powerConsumptionPerHour ?? undefined
        );

        if (!deviceTemplateId) {
          this.handleImportError(device, ImportErrorType.TEMPLATE_NOT_FOUND);
          continue;
        }

        if (!isNotEmpty(device.inventoryNumber)) {
          this.handleImportError(
            device,
            ImportErrorType.MISSING_INVENTORY_NUMBER
          );
          continue;
        }

        if (!isInventoryNumberValid(device.inventoryNumber)) {
          this.handleImportError(
            device,
            ImportErrorType.INVALID_INVENTORY_NUMBER
          );
          continue;
        }

        const isInventoryNumberUnique =
          !this.clientPanels.find(
            (val) =>
              val.inventoryNumber &&
              isNotEmpty(val.inventoryNumber) &&
              val.inventoryNumber === device.inventoryNumber
          ) &&
          !this.currentDevices.find(
            (val) => val.inventoryNumber === device.inventoryNumber
          );

        if (!isInventoryNumberUnique) {
          const conflictingPanel = this.clientPanels.find(
            (val) => val.inventoryNumber && isNotEmpty(val.inventoryNumber) && val.inventoryNumber === device.inventoryNumber
          );
          const conflictingCurrent = this.currentDevices.find(
            (val) => val.inventoryNumber === device.inventoryNumber
          );
          this.handleImportError(
            device,
            ImportErrorType.INVENTORY_NUMBER_ALREADY_EXISTS,
            false,
            undefined,
            conflictingPanel?.serialNumber ?? conflictingPanel?.inventoryNumber ?? conflictingCurrent?.inventoryNumber ?? device.inventoryNumber,
          );
          continue;
        }  

        const isQrCodeUnique = this.isQrCodeUnique(
          device,
          ImportType.CREATE_GENERIC_DEVICE
        );

        if (!isQrCodeUnique) {
          const statusKey = DUPLICATE_STATUS_KEY_ID+device.inventoryNumber;
          const isAlreadyProcessed = this.statuses.get(device.inventoryNumber);

          this.handleImportError(
            device,
            ImportErrorType.QR_CODE_ALREADY_EXISTS,
            false,
            isAlreadyProcessed ? statusKey : undefined,
            this.findConflictingQrCodeDevice(device),
          );

          continue;
        }  

        if (device.qrCode && !isQrCodeValid(device.qrCode)) {
          this.handleImportError(
            device,
            ImportErrorType.INVALID_QR_CODE
          );
          continue;
        }
        
        if (!this.isBranchNumberValid(device.homeLocation, undefined)) {
          this.handleImportError(device, ImportErrorType.BRANCH_NOT_FOUND);
          continue;
        }

        // A device that has just been created stands at its home base, so both
        // locations start out on the STANDORT column. Leaving the current one empty
        // would hide the device from every branch-filtered list.
        const homeLocation =
          device.homeLocation && isNotEmpty(device.homeLocation)
            ? device.homeLocation
            : null;

        const nextInternalDeviceNumber = maxInternalDeviceNumber + 1;
        const newDevice: NewGenericDevice = {
          categoryName: getCategoryName(this.deviceSettings, categoryId),
          templateId: deviceTemplateId,
          categoryId: categoryId,
          acquisitionDate: device.acquisitionDate,
          branch: homeLocation,
          homeLocation: homeLocation,
          internalNumber: nextInternalDeviceNumber + this.devicesToSave.length,
          inventoryNumber: device.inventoryNumber,
          qrCode: device.qrCode ?? null,
          serialNumber: device.serialNumber ?? null,
          manufacturer: device.manufacturer,
          knownSince: firestore.Timestamp.now(),
          electricityMeterManufacturer: device.electricityMeterManufacturer,
          deviceName: device.deviceName,
          availability: device.availability,
          powerConsumptionPerHour: device.powerConsumptionPerHour ?? null,
          selectedPowerConsumption: device.selectedPowerConsumption ?? null,
          note: device.note,
          lastMeterReading: device.lastMeterReading ?? null
        };

        const deviceToSave = createDevice(newDevice);

        if (!this.statuses.get(device.inventoryNumber)) {
          this.devicesToSave.push(filterOutUndefined(deviceToSave));
          this.statuses.set(device.inventoryNumber, ImportResult.NEW);
        }
        else {
          const statusKey = DUPLICATE_STATUS_KEY_ID+device.inventoryNumber;
          this.statuses.set(statusKey, ImportResult.DUPLICATE);
          this.noOfDuplicateDevices++;
          this.emitProgress(device, false, statusKey);
        }
      }
    }

    if (this.dryRun) {
      // Dry-run: emit progress for all validated devices without writing to Firestore
      for (const update of this.updatedIresDevices) {
        const clientPanel = this.clientPanels.find(
          (panel) => panel.serialNumber === update.serialNumber
        );
        if (clientPanel) {
          this.emitProgress(clientPanelToGenericDevice(clientPanel), true);
        }
      }
      for (const update of this.devicesToUpdate) {
        const device = this.devices.find(
          (vvv) => vvv.inventoryNumber === update.newDevice.inventoryNumber
        );
        if (device) {
          this.updatedDevices.push(update.newDevice);
          this.emitProgress(device);
        }
      }
      for (const device of this.devicesToSave) {
        this.newlyCreatedDevices.push(device);
        this.emitProgress(device);
      }
      return;
    }

    if ((this.newCategories.length || this.newTemplates.length) > 0) {
      try {
        await saveDeviceSettings(this.deviceSettings);
      } catch (e) {
        this.abortImport();
      }
    }

    if (this.updatedIresDevices.length > 0) {
      const batchSize = 100;
      const batches = splitIntoBatches(this.updatedIresDevices, batchSize);

      for (const batch of batches) {
        if (this.isAborted) {
          return;
        }
        try {
          await updateClientPanels(batch);
          
          for (const update of batch) {
            if (this.isAborted) {
              return;
            }
            
            const clientPanel = this.clientPanels.find(
              (panel) => panel.serialNumber === update.serialNumber
            );

            if (!clientPanel) {
              continue;
            }

            this.emitProgress(clientPanelToGenericDevice(clientPanel), true);
          }
        } catch (e) {
          // Batch failed — retry each item individually to isolate failures
          for (const update of batch) {
            if (this.isAborted) {
              return;
            }
            const clientPanel = this.clientPanels.find(
              (panel) => panel.serialNumber === update.serialNumber
            );
            if (!clientPanel) {
              continue;
            }
            try {
              await updateClientPanels([update]);
              this.emitProgress(clientPanelToGenericDevice(clientPanel), true);
            } catch {
              this.handleImportError(clientPanelToGenericDevice(clientPanel), ImportErrorType.SERVER_ERROR);
            }
          }
        }
      }
    }

    if (this.devicesToUpdate.length > 0) {
      const batchSize = 100;
      const batches = splitIntoBatches(this.devicesToUpdate, batchSize);

      for (const batch of batches) {
        if (this.isAborted) {
          return;
        }
        try {
          await updateDevices(batch);
          this.updatedDevices.push(
            ...batch.map((update) => {
              return update.newDevice;
            })
          );
          for (const update of batch) {
            if (this.isAborted) {
              return;
            }
            const device = this.devices.find(
              (vvv) => vvv.inventoryNumber === update.newDevice.inventoryNumber
            );
            if (!device) {
              continue;
            }
            this.emitProgress(device);
          }
        } catch (e) {
          // Batch failed — retry each item individually to isolate failures
          for (const update of batch) {
            if (this.isAborted) {
              return;
            }
            const device = this.devices.find(
              (vvv) => vvv.inventoryNumber === update.newDevice.inventoryNumber
            );
            if (!device) {
              continue;
            }
            try {
              await updateDevice(update.oldDevice, update.newDevice);
              this.updatedDevices.push(update.newDevice);
              this.emitProgress(device);
            } catch {
              this.handleImportError(device, ImportErrorType.SERVER_ERROR);
            }
          }
        }
      }
    }

    if (this.devicesToSave.length > 0) {
      const batchSize = 100;
      const batches = splitIntoBatches(this.devicesToSave, batchSize);
      for (const batch of batches) {
        if (this.isAborted) {
          return;
        }
        try {
          await saveDevices(batch);
          for (const device of batch) {
            if (this.isAborted) {
              return;
            }
            this.newlyCreatedDevices.push(device);
            this.emitProgress(device);
          }
        } catch (e) {
          // Batch failed — retry each item individually to isolate failures
          for (const device of batch) {
            if (this.isAborted) {
              return;
            }
            try {
              await saveDevices([device]);
              this.newlyCreatedDevices.push(device);
              this.emitProgress(device);
            } catch {
              const data = this.devices.find(
                (vvv) => vvv.inventoryNumber === device.inventoryNumber
              );
              if (!data) {
                continue;
              }
              this.handleImportError(device, ImportErrorType.SERVER_ERROR);
            }
          }
        }
      }
    }
  }

  async getDeviceCategoryId(categoryName: string) {
    const categoryId = getCategoryIdFromName(this.deviceSettings, categoryName);
    
    if (categoryId) {
      return categoryId;
    }
    
    if (!this.clientSettings.newCategoryAllowed || !defineCategoriesAndTemplatesClientAccess()) {
      return null;
    }
    
    try {
      const settingsUpdate = await addNewCategory(
        categoryName,
        this.deviceSettings,
        false
      );

      if (settingsUpdate) {
        this.deviceSettings = settingsUpdate;
        const newCategoryId = getCategoryIdFromName(settingsUpdate, categoryName);

        if (newCategoryId) {
          this.newCategories.push(newCategoryId);
          return newCategoryId;
        }
      }
    } catch (e) {
      // Intentionally empty
    }

    return null;
  }

  async getDeviceTemplateId(
    templateName: string,
    categoryId: string,
    devicePowerUsageType?: DevicePowerUsageType,
    powerConsumptionPerHour?: number,
  ) {
    const templateId = getDeviceTemplateIdFromName(
      this.deviceSettings,
      categoryId,
      templateName
    );
    if (templateId) {
      return templateId;
    }
    if (!this.clientSettings.newTemplateAllowed || !defineCategoriesAndTemplatesClientAccess()) {
      return null;
    }
    try {
      const settingsUpdate = await addEditTemplate(
        false,
        categoryId,
        templateName,
        devicePowerUsageType ?? DevicePowerUsageType.NONE,
        this.deviceSettings,
        undefined,
        powerConsumptionPerHour
      );
      if (!settingsUpdate) {
        return null;
      }

      this.deviceSettings = settingsUpdate;
      const newTemplate = getDeviceTemplateIdFromName(
        this.deviceSettings,
        categoryId,
        templateName
      );

      if (newTemplate) {
        this.newTemplates.push(newTemplate);
        return newTemplate;
      }
      return null;
    } catch (e) {
      return null;
    }
  }
  /**
   * Aborts the import process.
   * 
   * WARNING: Aborting does NOT roll back devices already written to Firestore in previous batches.
   * Devices saved/updated before the abort call remain persisted. Only unprocessed devices and
   * the current/subsequent batches are skipped.
   */
  public abortImport() {
    this.isAborted = true;
  }

  public async startImport() {
    return this.importDevices();
  }

  public async updateImportHistory(data: ImportProgress) {
    if (!data.report) {
      return;
    }
    const importHistory = createImportHistory(
      data.report,
      data.updatedDevices,
      data.newDevices,
      data.totalDevices,
      data.errors.length,
      this.fileName
    );
    try {
      await storeImportHistory(importHistory);
    } catch (e) {
      // intentionally empty
    }
  }
}
